import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { Box, Typography } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../../components/UIToolkit/Badges/MetaChip";
import { DayToggleGrid, type DayToggleGroup } from "../../../../components/UIToolkit/Clickable/DayToggleGrid";
import { SystemChips } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { AccentIconTile } from "../../../../components/UIToolkit/molecules/AccentIconTile";
import Dialog from "../../../../components/UIToolkit/Popups/Dialog";
import { ACCENT_BLUE, COVERAGE, FONT_SIZE, GRID_RADIUS, INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import type { GridDates } from "../types";
import { type GapTone, gapToneStyle } from "../utils/gapTones";
import { dayDateOf, formatGridDayDate, formatWeekHeader } from "../utils/gridDates";
import { cellKey, DAYS_PER_WEEK } from "../utils/suggestedHires";
import { dayKeyAt } from "../utils/weekStart";

type Props = {
	open: boolean;
	onClose: () => void;
	/**
	 * EVERY shift type the job runs, not just the ones the Hire tab's filter has in scope: this dialog
	 * is where you say what you are hiring for, and a filter narrowing what you are *looking* at must not
	 * narrow what you can create.
	 */
	shiftLabels: string[];
	/** Label → that template's real hours, for the row's pill once the hire exists. */
	templateHours: Record<string, number>;
	gridWeeks: number;
	weekNumbers: number[];
	weekStartDay: number;
	/** REQUIRED: the week captions and day chips here name the same dates as the grid below the dialog. */
	dates: GridDates;
	/** The department's own timezone — which week counts as ACTIVE is a question about its day. */
	timezone: string;
	/** Every job this department can hire into, for the ROLE row. */
	jobOptions: { jobId: string; displayName: string }[];
	/** The job the hire is created for — the page's own selected job. */
	jobId: string;
	jobDisplayName: string;
	/**
	 * Switch the page to another job. Picking a ROLE here moves the WHOLE page, because a hire only
	 * exists inside a job: the grid, the suggestions and the storage keys are all job-scoped, so a hire
	 * created for a job the page is not on would be filed under the wrong one.
	 */
	onJobChange: (jobId: string) => void;
	/**
	 * Whether a given day still has an unfilled gap on a given shift, so the day chips can mark the days
	 * worth choosing. Same tone function the grid's cells and the row markers use, so the dots here can
	 * never disagree with the numbers below.
	 */
	gapToneFor: (shiftLabel: string, key: number) => GapTone;
	/** Creates the hire. `applied` counts it as coverage in the grid straight away. */
	onCreate: (input: { name?: string; shiftLabel: string; shiftLength: number; shiftByDay: Record<number, string>; applied: boolean }) => void;
};

const SECTION_LABEL_SX = {
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 700,
	letterSpacing: "0.08em",
	textTransform: "uppercase" as const,
	color: INK.MUTED,
};

/**
 * RECTANGULAR, at the module's one chip radius — the same 6px the row chips, the legend chips, the
 * mode/tab switches and the job pill already use. A full pill reads as something you can tap away;
 * these are a choice you are making.
 */
const CHIP_SELECTED_SX = {
	"&.MuiChip-root": {
		borderRadius: GRID_RADIUS.CHIP,
		backgroundColor: ACCENT_BLUE.TINT,
		borderColor: ACCENT_BLUE.BORDER,
		color: ACCENT_BLUE.INK,
		fontWeight: 700,
	},
	"&.MuiChip-root:hover": { backgroundColor: ACCENT_BLUE.TINT, color: ACCENT_BLUE.INK },
} as const;

const CHIP_UNSELECTED_SX = {
	"&.MuiChip-root": { borderRadius: GRID_RADIUS.CHIP, backgroundColor: SURFACE, borderColor: NEUTRAL[300], color: INK.MUTED, fontWeight: 500 },
	"&.MuiChip-root:hover": { backgroundColor: NEUTRAL[50], color: INK.SECONDARY },
} as const;

/** The legend's dot, and the marker on a day that still has a gap — one colour, stated once. */
const GapDot: FC = () => (
	<Box
		component="span"
		aria-hidden
		sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: COVERAGE.WARNING_MARKER, border: `1px solid ${COVERAGE.CRITICAL_MARKER}` }}
	/>
);

/**
 * A day with a gap wears the SHORT wash — the pale yellow fill and amber ink, straight off
 * `gapToneStyle`, which is the same function the grid's cells and the panel's `Short` legend chip read.
 * So "the yellow in this dialog" and "the yellow in the grid" are one value by construction, and a
 * change to the scale moves all three together.
 *
 * `short` rather than each day's own tier: this dialog answers "is there still a gap here", and grading
 * the shortfall belongs to the grid, where the number sits beside the colour.
 */
const GAP_DAY_STYLE = gapToneStyle("short");
const GAP_DAY_TONE = { bg: GAP_DAY_STYLE.bg as string, ink: GAP_DAY_STYLE.ink, border: GAP_DAY_STYLE.bar };

/**
 * "Add hire manually" — one person, built by hand.
 *
 * It exists for the two cases the generator cannot answer: somebody already hired who is not in the
 * schedule template yet (in orientation, say), and planning ahead on a job the packer says needs
 * nobody. So it takes the same four facts a generated row carries — a name, one shift type, the days,
 * and the job — and nothing else.
 *
 * **No ROLE row**, deliberately, though the design has one. Role there is this page's JOB, and the job
 * pill in the header already picks it: the whole page (the grid, the suggestions, the stored edits, the
 * storage keys) is scoped to one job at a time, so a second job control inside this dialog would either
 * contradict the header or silently move the page out from under the user. The job is stated instead —
 * in the summary line, and on the row this creates.
 *
 * Every day that still has a gap on the chosen shift is dotted, so the choice is informed rather than
 * blind, and the dots come from the same tone function as the grid's own cells.
 */
const AddHireDialog: FC<Props> = ({
	open,
	onClose,
	shiftLabels,
	templateHours,
	gridWeeks,
	weekNumbers,
	weekStartDay,
	dates,
	timezone,
	jobOptions,
	jobId,
	jobDisplayName,
	onJobChange,
	gapToneFor,
	onCreate,
}) => {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	// No fallback: the caller only renders this dialog once the payload has produced template labels, so
	// the first is a real one. A dialog with nothing to pick has nothing to save either.
	const [shiftLabel, setShiftLabel] = useState(shiftLabels[0]);
	const [selectedDays, setSelectedDays] = useState<number[]>([]);

	const activeShift = shiftLabels.includes(shiftLabel) ? shiftLabel : shiftLabels[0];

	const groups: DayToggleGroup[] = useMemo(
		() =>
			Array.from({ length: gridWeeks }, (_, weekIdx) => ({
				key: weekIdx,
				label: formatWeekHeader(dates, weekIdx, weekNumbers[weekIdx], timezone, t),
				cells: Array.from({ length: DAYS_PER_WEEK }, (_, column) => {
					const dayIdx = (column + weekStartDay) % DAYS_PER_WEEK;
					const key = cellKey(weekIdx, dayIdx);
					const dayKey = dayKeyAt(column, weekStartDay);
					const tone = gapToneFor(activeShift, key);
					return {
						key,
						label: t(`hiring.day.${dayKey}`),
						selected: selectedDays.includes(key),
						ariaLabel: t("hiring.hire.row.dayToggle", {
							day: t(`hiring.day.${dayKey}`),
							date: formatGridDayDate(dayDateOf(dates, weekIdx, dayIdx, weekStartDay)),
							week: weekNumbers[weekIdx],
						}),
						// Only "there is still a gap here" is marked — which of two shades of short it is
						// belongs to the grid, where the number is next to it.
						marker:
							tone === "neutral" ? undefined : { color: COVERAGE.WARNING_MARKER, ringColor: COVERAGE.CRITICAL_MARKER, tooltip: t("hiring.hire.add.gapDay") },
						tone: tone === "neutral" ? undefined : GAP_DAY_TONE,
						dataTestId: `hiring-hire-add-day-${key}`,
					};
				}),
			})),
		[gridWeeks, weekNumbers, weekStartDay, dates, timezone, selectedDays, activeShift, gapToneFor, t],
	);

	/**
	 * The readout's subject: the name being typed, or "this hire" until there is one.
	 *
	 * Four whole phrases rather than concatenated fragments — the no-role case exists because the
	 * department catalog can carry no name for an eligible job, and a sentence assembled from pieces
	 * cannot be reordered by a translator.
	 */
	const typedName = name.trim();
	const summarySentence = jobDisplayName
		? typedName === ""
			? t("hiring.hire.add.summary", { role: jobDisplayName, shift: activeShift })
			: t("hiring.hire.add.summaryNamed", { name: typedName, role: jobDisplayName, shift: activeShift })
		: typedName === ""
			? t("hiring.hire.add.summaryNoRole", { shift: activeShift })
			: t("hiring.hire.add.summaryNamedNoRole", { name: typedName, shift: activeShift });

	const reset = () => {
		setName("");
		setShiftLabel(shiftLabels[0]);
		setSelectedDays([]);
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleConfirm = () => {
		const shiftByDay: Record<number, string> = {};
		for (const key of selectedDays) {
			shiftByDay[key] = activeShift;
		}
		onCreate({
			// Blank is passed through as blank, NOT as a placeholder: an unnamed row is then named from the
			// shape of its week like a generated one ("Weekend", "Weekdays 2"), which is what tells two
			// otherwise-identical manual hires apart. A typed name is the user's and is never re-derived.
			name: name.trim(),
			shiftLabel: activeShift,
			shiftLength: templateHours[activeShift],
			shiftByDay,
			// "Save & Apply" — the person counts as coverage immediately, which is the whole reason this
			// dialog exists for the already-hired case.
			applied: true,
		});
		reset();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<AccentIconTile size="sm" color={ACCENT_BLUE.INK} tint={ACCENT_BLUE.TINT}>
						<PersonAddAlt1OutlinedIcon />
					</AccentIconTile>
					{t("hiring.hire.add.title")}
				</Box>
			}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
						<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.add.nameLabel")}</Typography>
						<SystemValidationTextField
							value={name}
							placeholder={t("hiring.hire.add.namePlaceholder")}
							onChange={(event) => setName(event.target.value)}
							reserveHeightForHelperText={false}
							dataTestId="hiring-hire-add-name"
						/>
					</Box>

					{/* ROLE and SHIFT side by side, as the design has them — two facts about the same person. */}
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
							<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.add.jobLabel")}</Typography>
							<SystemChips
								items={jobOptions.map((job) => ({ value: job.jobId, label: job.displayName }))}
								selectedItems={jobOptions.filter((job) => job.jobId === jobId).map((job) => ({ value: job.jobId, label: job.displayName }))}
								variant="outlined"
								selectedSx={CHIP_SELECTED_SX}
								unselectedSx={CHIP_UNSELECTED_SX}
								forceFitContent
								rootSx={{ justifyContent: "flex-start" }}
								dataTestId="hiring-hire-add-roles"
								getItemDataTestId={(item) => `hiring-hire-add-role-${item.value}`}
								/**
								 * Picking a role moves the whole page to that job — see `onJobChange`. The day
								 * selection is dropped with it: the days were chosen against the previous job's
								 * gaps and its templates, and keeping them would carry one job's answer into
								 * another job's question.
								 */
								onChange={(item) => {
									if (item.value === jobId) {
										return;
									}
									setSelectedDays([]);
									onJobChange(item.value);
								}}
							/>
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 0 }}>
							<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.add.shiftLabel")}</Typography>
							<SystemChips
								items={shiftLabels.map((label) => ({ value: label, label }))}
								selectedItems={[{ value: activeShift, label: activeShift }]}
								variant="outlined"
								selectedSx={CHIP_SELECTED_SX}
								unselectedSx={CHIP_UNSELECTED_SX}
								forceFitContent
								rootSx={{ justifyContent: "flex-start" }}
								dataTestId="hiring-hire-add-shifts"
								getItemDataTestId={(item) => `hiring-hire-add-shift-${item.value}`}
								// A hand-added person starts on ONE shift type — this dialog answers "what am I
								// hiring for", and a second type is a widening you do afterwards on the row itself.
								// So this is a single-choice group and re-clicking the chosen chip changes nothing.
								// (Unrelated to Settings' SHIFT TYPES PER PERSON, which shapes what we SUGGEST.)
								onChange={(item) => setShiftLabel(item.value)}
							/>
						</Box>
					</Box>

					{/* The tour's anchor is the days section, not the whole form: with dozens of shift types the form is taller
					    than a laptop screen, which pushed the tour's tooltip below the fold. */}
					<Box data-annie-target="hiring-hire-add-days" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
							<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.add.daysLabel")}</Typography>
							<MetaChip
								label={t("hiring.hire.add.gapLegend")}
								icon={<GapDot />}
								// The same wash as the days it names — see `GAP_DAY_TONE`.
								customTone={GAP_DAY_TONE}
								sx={{ borderRadius: GRID_RADIUS.CHIP, fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700 }}
								dataTestId="hiring-hire-add-gap-legend"
							/>
						</Box>
						<DayToggleGrid
							groups={groups}
							stretch
							size="large"
							tone="accent"
							onToggle={(cell) =>
								setSelectedDays((prev) => (prev.includes(Number(cell.key)) ? prev.filter((key) => key !== Number(cell.key)) : [...prev, Number(cell.key)]))
							}
							dataTestId="hiring-hire-add-days"
						/>
					</Box>

					{/* The readout, so Save is never a guess about what was selected. */}
					<Box
						data-testid="hiring-hire-add-summary"
						sx={{ px: 2, py: 1.5, borderRadius: 1.5, backgroundColor: ACCENT_BLUE.TINT, border: `1px solid ${ACCENT_BLUE.BORDER}` }}
					>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: ACCENT_BLUE.INK }}>
							<Box component="span" sx={{ fontWeight: 700 }}>
								{t("hiring.hire.add.summaryCount", { count: selectedDays.length })}
							</Box>{" "}
							{summarySentence}
						</Typography>
					</Box>
				</Box>
			}
			cancel={t("cancel")}
			confirm={t("hiring.hire.add.save")}
			confirmLeadingIcon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
			confirmSx={{ width: "100%", backgroundColor: ACCENT_BLUE.MAIN, color: ACCENT_BLUE.ON_ACCENT, "&:hover": { backgroundColor: ACCENT_BLUE.DARK } }}
			// A person needs a name: it is how you tell two hires apart on the panel, and how the row you are
			// about to create is identified. The days stay optional — you can pick them on the row itself.
			confirmDisabled={typedName === ""}
			confirmTooltip={typedName === "" ? t("hiring.hire.add.noNameTooltip") : undefined}
			/**
			 * One row, not a stacked column: the two are a pair — a bordered Cancel beside the wide action
			 * it belongs to.
			 *
			 * The `flex: 1` is on the ROW's last child, not in `confirmSx`: a `confirmTooltip` wraps the
			 * button in a span, and growing the button inside a shrink-wrapped wrapper grows nothing.
			 */
			cancelButtonVariant="secondary"
			actionsSx={{ px: 3, pb: 3, gap: 1.5, "& > :last-child": { flex: 1 } }}
			onConfirm={handleConfirm}
			dataTestId="hiring-hire-add-dialog"
		/>
	);
};

export default AddHireDialog;
